import { useCallback, useEffect, useState } from "react";
import { Crown, Mail } from "lucide-react";
import { toast } from "sonner";
import { getAuthHeaders } from "@/lib/auth-fetch";
import { API_BASE } from "@/lib/utils";

function apiErrorDetail(data, status) {
  const d = data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || x).join("; ");
  return `Failed to load (${status})`;
}

export default function OwnershipTransferPanel() {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [successorEmail, setSuccessorEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/ownership-transfer`, { headers: getAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(apiErrorDetail(data, res.status));
      setState(data);
      if (data?.pending?.successorEmail) {
        setSuccessorEmail(data.pending.successorEmail);
      }
    } catch (err) {
      toast.error("Ownership settings unavailable", { description: err?.message });
      setState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  if (loading) {
    return (
      <div className="surface p-6 max-w-xl text-sm text-[var(--text-tertiary)]">
        Loading ownership settings…
      </div>
    );
  }

  if (!state) {
    return (
      <div className="surface p-6 max-w-xl text-sm text-[var(--text-secondary)]">
        Ownership settings could not be loaded. Refresh the page or sign in again.
      </div>
    );
  }

  if (!state.isOwner) {
    return (
      <div className="surface p-6 max-w-xl space-y-2" data-testid="ownership-transfer-readonly">
        <div className="flex items-center gap-2 text-overline">
          <Crown size={12} /> Account owner
        </div>
        <p className="text-sm text-[var(--text-secondary)]">
          Workspace owner:{" "}
          <span className="font-mono text-[var(--text-primary)]">{state?.ownerEmail || "—"}</span>
        </p>
        <p className="text-xs text-[var(--text-tertiary)]">
          Only the owner can transfer this account. Contact them if you need a change.
        </p>
      </div>
    );
  }

  const successors = Array.isArray(state.eligibleSuccessors) ? state.eligibleSuccessors : [];
  const pending = state.pending;

  const startTransfer = async () => {
    const email = String(successorEmail || "").trim().toLowerCase();
    if (!email) {
      toast.error("Choose a successor", { description: "Select a team member to receive ownership." });
      return;
    }
    if (
      !window.confirm(
        `Send an ownership invitation to ${email}? They must accept before becoming owner. You will become an admin after they confirm.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/ownership-transfer`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "content-type": "application/json" },
        body: JSON.stringify({ successorEmail: email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(apiErrorDetail(data, res.status));
      toast.success(data?.emailSent ? "Invitation emailed" : "Invitation created", {
        description: data?.emailSent
          ? `${email} must accept the link before becoming owner.`
          : `${email} — email delivery failed; resend from Team or try again.`,
      });
      await reload();
    } catch (err) {
      toast.error("Could not start transfer", { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  const cancelTransfer = async () => {
    if (!pending) return;
    if (!window.confirm("Cancel the pending ownership transfer?")) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/ownership-transfer`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(apiErrorDetail(data, res.status));
      toast.success("Transfer invitation cancelled");
      await reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface p-6 max-w-xl space-y-4" data-testid="ownership-transfer-panel">
      <div className="flex items-center gap-2 text-overline">
        <Crown size={12} /> Transfer ownership
      </div>
      <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
        Move billing and full control to another admin. The nominee must confirm by email before they become owner; you will remain on the team as an admin.
      </p>

      {pending ? (
        <div className="border border-[var(--border-default)] p-4 space-y-3 bg-[var(--bg-elev)]">
          <div className="flex items-start gap-2 text-sm">
            <Mail size={16} className="shrink-0 mt-0.5 text-[var(--primary)]" />
            <div>
              <p className="font-medium">Invitation pending</p>
              <p className="text-xs text-[var(--text-tertiary)] mt-1 font-mono">
                {pending.successorEmail} · expires {new Date(pending.expiresAt).toLocaleString()}
              </p>
            </div>
          </div>
          <button type="button" className="btn-ghost text-xs text-red-400" onClick={cancelTransfer} disabled={busy}>
            Cancel invitation
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="text-overline">New owner (team member)</label>
            {successors.length ? (
              <select
                className="input-dark mt-2 w-full"
                value={successorEmail}
                onChange={(e) => setSuccessorEmail(e.target.value)}
                data-testid="ownership-successor-select"
              >
                <option value="">Select member…</option>
                {successors.map((m) => (
                  <option key={m.email} value={m.email}>
                    {m.name} ({m.email}) — {m.role}
                  </option>
                ))}
              </select>
            ) : (
              <p className="mt-2 text-xs text-[var(--text-tertiary)]">
                Invite at least one admin under Team &amp; roles before transferring ownership.
              </p>
            )}
          </div>
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={startTransfer}
            disabled={busy || !successorEmail || !successors.length}
            data-testid="ownership-transfer-start"
          >
            Send ownership invitation
          </button>
        </div>
      )}
    </div>
  );
}
